/**
 * Olfacy wishlist page: renders saved products (localStorage) as real Olfacy cards,
 * fetched in parallel through the Section Rendering API. Removing a heart removes the card.
 */
const WISHLIST_KEY = 'olfacy:wishlist';
const WISHLIST_EVENT = 'olfacy:wishlist-change';
const SECTION_ID = 'olfacy-product-card-render';

const readWishlist = () => {
  try {
    const list = JSON.parse(localStorage.getItem(WISHLIST_KEY) || '[]');
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
};

class OlfacyWishlistPage extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-grid]');
    this.empty = this.querySelector('[data-empty]');
    this.loading = this.querySelector('[data-loading]');
    this.count = this.querySelector('[data-count]');
    this.root = this.dataset.root || '/';
    this.onChange = () => this.prune();
    document.addEventListener(WISHLIST_EVENT, this.onChange);
    this.load();
  }

  disconnectedCallback() {
    document.removeEventListener(WISHLIST_EVENT, this.onChange);
  }

  async load() {
    const handles = readWishlist();
    if (!handles.length) return this.showEmpty();

    this.loading.hidden = false;
    const results = await Promise.all(handles.map((handle) => this.fetchCard(handle)));
    this.loading.hidden = true;

    const found = [];
    const fragment = document.createDocumentFragment();
    results.forEach((html, i) => {
      if (!html) return;
      const li = document.createElement('li');
      li.dataset.handle = handles[i];
      li.innerHTML = html;
      fragment.append(li);
      found.push(handles[i]);
    });
    this.grid.append(fragment);

    // Drop products that no longer exist (deleted or unpublished)
    if (found.length !== handles.length) {
      try {
        localStorage.setItem(WISHLIST_KEY, JSON.stringify(found));
      } catch {}
    }
    this.update();
  }

  async fetchCard(handle) {
    try {
      const url = `${this.root}products/${encodeURIComponent(handle)}?section_id=${SECTION_ID}`;
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) return null;
      const html = (await response.text()).trim();
      return html.includes('ocard') ? html : null;
    } catch {
      return null;
    }
  }

  prune() {
    const saved = new Set(readWishlist());
    this.grid.querySelectorAll('li[data-handle]').forEach((li) => {
      if (!saved.has(li.dataset.handle)) li.remove();
    });
    this.update();
  }

  update() {
    const n = this.grid.children.length;
    if (!n) return this.showEmpty();
    this.empty.hidden = true;
    this.count.hidden = false;
    this.count.textContent = `${n} ${n === 1 ? 'item' : 'items'}`;
  }

  showEmpty() {
    this.loading.hidden = true;
    this.count.hidden = true;
    this.empty.hidden = false;
  }
}

if (!customElements.get('olfacy-wishlist-page')) {
  customElements.define('olfacy-wishlist-page', OlfacyWishlistPage);
}
